<template>
  <div class="rounded-md bg-white p-6 shadow-md">
    <div class="space-y-4">
      <h2 class="mb-2 text-center text-xl font-semibold text-gray-700">
        <span v-if="deuda"> {{ deuda.descripcion }} </span>
      </h2>
      <transition enter-active-class="transition-opacity duration-300"
        leave-active-class="transition-opacity duration-300" enter-class="opacity-0" leave-to-class="opacity-0"
        mode="out-in">
        <Cargando v-if="isLoading" key="1" />
        <Empty key="2" v-else-if="errorCarga" :msg="errorCarga" />
        <Empty key="3" v-else-if="!deuda" />
        <div v-else key="4" class="bg-white p-4 shadow ring-1 ring-black ring-opacity-5 md:rounded-md md:p-6">
          <PaymentHelpModal class="z-100 max-h-full overflow-y-scroll" :user="user" :is-open="showHelpModal"
            :proveedores="proveedoresPago" @modal:close="showHelpModal = false" />

          <div class="mb-2 flex justify-end">
            <button v-if="permitePagoElectronico"
              class="inline-flex items-center rounded-full bg-white text-sm font-bold text-blue-brand-700 focus:outline-none"
              title="Ayuda" @click="showHelpModal = !showHelpModal">
              <OutlineQuestionMarkCircleIcon class="h-6 w-6" />
            </button>
          </div>
          <div class="text-md mb-6 rounded-md bg-gray-100 px-4 py-4 text-left text-gray-700">
            Si ya seleccionó un medio de pago previamente para este comprobante, el nuevo medio de
            pago no invalida el anterior.
          </div>
          <div class="space-y-4 px-1">
            <div class="text-lg font-medium text-gray-700">
              Monto a abonar: {{ deuda.vencimiento_vigente.monto | currency }}
            </div>
            <SeleccionModoPago :monto="deuda.vencimiento_vigente.monto" :tipoDeuda="deuda.tipo.id.trim()"
              v-model="medioPago" @permitePagoElectronico:update="permitePagoElectronico = $event" />
            <p v-if="medioPago && !validMedioPago" class="mt-2 text-xs font-semibold text-red-600">
              El medio de pago seleccionado ya existe como medio de pago para este comprobante.
            </p>
            <div class="flex flex-row-reverse">
              <NextStepButton :valid="validMedioPago" @click.native="showConfirmModal = true">Confirmar nuevo medio de
                pago</NextStepButton>
            </div>
          </div>
          <ConfirmarNuevoPagoModal v-if="medioPago" :isOpen="showConfirmModal" :medioPago="medioPago" :deuda="deuda"
            @modal:confirm="afterCreation" @modal:cancel="showConfirmModal = false" />
        </div>
      </transition>
      <transition enter-active-class="transition-opacity duration-300"
        leave-active-class="transition-opacity duration-300" enter-class="opacity-0" leave-to-class="opacity-0"
        mode="out-in">
        <div v-if="!isLoading" class="flex justify-center">
          <button
            class="mx-2 rounded-md border border-gray-300 bg-white px-4 py-2 text-base font-medium text-gray-700 shadow-sm transition duration-150 ease-in-out hover:bg-cool-gray-200 focus:outline-none focus:ring focus:ring-blue-200 focus:ring-opacity-50 active:bg-cool-gray-200"
            @click="volver">
            Volver
          </button>
        </div>
      </transition>
    </div>
  </div>
</template>

<script>
import { useService } from '@colescba/app-vue'
import { useSession } from '@colescba/ui'

import ConfirmarNuevoPagoModal from '@/components/ConfirmarNuevoPagoModal'
import Cargando from '@/components/Core/Cargando'
import Empty from '@/components/Core/Empty'
import SeleccionModoPago from '@/components/SeleccionModoPago'
import { REFERENCIA_COMPROBANTES } from '@/util/comprobantes'
import { proveedoresPagoDefault, proveedorParagraphs } from '@colescba/ui'
import { mapState } from 'vuex'

export default {
  name: 'PagarComprobante',

  components: { Cargando, Empty, ConfirmarNuevoPagoModal, SeleccionModoPago },

  props: {
    idDeuda: { type: [String, Number], required: true },
  },

  mixins: [useService('portalPagos'), useSession()],

  data() {
    return {
      deuda: null,
      isLoading: true,
      medioPago: null,
      showConfirmModal: false,
      showHelpModal: false,
      proveedoresPago: null,
      permitePagoElectronico: false,
      errorCarga: null,
    }
  },

  computed: {
    ...mapState({
      formularios: state => state.app.formularios,
    }),
    formulario() {
      return this.formularios.find(f => f.tipoDeuda == this.deuda?.tipo.id.trim())
    },
    validMedioPago() {
      const id = this.medioPago?.id.trim()

      return Boolean(
        id === 'MBP' ||
        id === 'PNET' ||
        (this.medioPago && !this.deuda.publicaciones.some(p => p.medio_pago.id.trim() === id)) ||
        id === 'CTANOT',
      )
    },
  },

  async mounted() {
    try {
      this.deuda = await this.portalPagos.getDeuda(this.user.cuitCuil.numero, this.idDeuda)
    } catch (e) {
      this.deuda = null
      const status = e?.response?.status || e?.status
      const body = e?.response?.data || e?.data || {}
      const msg =
        body.errors && body.errors.length ? body.errors[0] : body.message || 'Error al cargar el comprobante'
      this.errorCarga =
        status === 404
          ? 'No es posible realizar el pago. Este comprobante está asociado a otro usuario.'
          : msg
      this.isLoading = false
      return
    }

    const referencia = this.obtenerReferenciaComprobante()
    if (referencia) {
      await this.$store.dispatch('app/actualizarCajaForm', referencia)
    }
    this.buildProveedoresPago()
    this.isLoading = false
  },

  methods: {
    volver() {
      if (this.errorCarga) {
        this.$router.push({ name: 'comprobantes' })
      } else {
        this.$router.go(-1)
      }
    },
    obtenerReferenciaComprobante() {
      const primerosTresDigitos = this.deuda.comprobante.numero_comprobante.substring(0, 3)
      return (
        REFERENCIA_COMPROBANTES.find(item => {
          return item.referencia.toString() === primerosTresDigitos
        }) || null
      )
    },
    afterCreation() {
      this.showConfirmModal = false
      this.$toast.open({
        message: 'Medio de pago generado correctamente.',
        type: 'success',
        duration: 2500,
        dismissible: true,
      })
      this.$router.push({ name: 'comprobantes' })
    },

    buildProveedoresPago() {
      this.proveedoresPago = proveedoresPagoDefault

      this.proveedoresPago.BANELCO.paragraphs.lines = proveedorParagraphs(
        this.formulario.pagoElectronico.BANELCO.rubro,
        this.formulario.pagoElectronico.BANELCO.ente,
        this.formulario.pagoElectronico.BANELCO.campoCPE,
      )

      this.proveedoresPago.LINK.paragraphs.lines = proveedorParagraphs(
        this.formulario.pagoElectronico.LINK.rubro,
        this.formulario.pagoElectronico.LINK.ente,
        this.formulario.pagoElectronico.LINK.campoCPE,
      )
    },
  },

  watch: {
    showHelpModal(newValue, oldValue) {
      if (newValue) {
        document.querySelector('body').classList.add('overflow-y-hidden')
      } else {
        document.querySelector('body').classList.remove('overflow-y-hidden')
      }
    },
  },

  beforeDestroy() {
    document.querySelector('body').classList.remove('overflow-y-hidden')
  },
}
</script>
